import { useEffect, useState } from 'react';
import { LayerPaths, buildLayerUrl, useMapStore, useUIStore } from '@uofu-uss-gis/ui-react';
import * as UiReact from '@uofu-uss-gis/ui-react';
import { useAuthStore } from '../../store/authStore';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import Point from '@arcgis/core/geometry/Point';
import { normalizeBuildingCode } from '../../utils/classUtils';

const MY_CLASSES_PINS_LAYER_ID = 'my-classes-pins-layer';
const MULTIPIN_ZOOM_THRESHOLD = 17;
const UNMAPPED_BUILDING_CODES = new Set(['TBA', '', 'CANVAS', 'ONLN', 'SANDY', 'ST. GEORGE']);

// ---------------------------------------------------------------------------
// Debug helpers
// ---------------------------------------------------------------------------

/**
 * Whether verbose pin diagnostics should be emitted. Toggled at runtime by
 * setting `localStorage.debugMyClassesPins = 'true'` in the browser console.
 */
const isPinsDebugEnabled = () => (
  typeof window !== 'undefined' && localStorage?.getItem('debugMyClassesPins') === 'true'
);

/** Debug-only logger. No-op unless the debug flag is enabled. */
const debugLog = (...args: any[]) => {
  if (isPinsDebugEnabled()) {
    console.warn('[MyClassesPins DEBUG]', ...args);
  }
};

/**
 * Layer ids registered by the indoors plugin. Exposed via a debug helper so
 * developers can introspect / toggle visibility while troubleshooting indoor
 * pin placement.
 */
const INDOORS_LAYER_IDS = [
  'indoors-details',
  'indoors-units',
  'indoors-levels',
  'indoors-facilities',
  'indoors-sites',
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single class pin tied to a specific building. */
interface BuildingPin {
  /** Normalized building abbreviation (e.g. "WEB", "LCB"). */
  bldgCd: string;
  /** Index of this class within the user's class list (drives pin number 1-9). */
  classIndex: number;
  /** Raw class record from the auth/class payload. */
  classInfo: any;
}

/** Loose shape covering the various ways a "level" can be represented. */
interface LevelLike {
  levelId?: string;
  id?: string;
  name?: string;
  shortName?: string;
  attributes?: {
    LEVEL_ID?: string;
    level_short?: string;
    BldgLevel?: string;
    NAME?: string;
    NAME_SHORT?: string;
  };
}

/** Loose shape for the currently-selected indoor facility. */
interface SelectedFacilityLike {
  facilityId?: string | number;
  id?: string | number;
  bldg_num?: string | number;
  building_number?: string | number;
  attributes?: {
    FACILITY_ID?: string | number;
    bldg_num?: string | number;
    building_number?: string | number;
  };
}

/** A unit (room) feature paired with its resolved level metadata. */
interface UnitMatch {
  feature: any;
  level?: any;
}

// ---------------------------------------------------------------------------
// Popup / symbol helpers
// ---------------------------------------------------------------------------

// NOTE: We intentionally do NOT attach an Esri popupTemplate to class pins.
// Clicking a pin opens the in-app My Classes detail panel via the map
// click handler in the component below, providing a consistent UX with
// the rest of the app.

/**
 * When multiple pins share the same anchor point we fan them out so they're
 * each individually clickable. Returns an (x, y) screen-space offset for the
 * `index`-th pin in a group of `total` pins.
 *
 * - 1 pin: no offset
 * - 2 pins: side-by-side
 * - 3+ pins: arranged on a circle, starting from the top (-π/2)
 */
const getPinOffset = (index: number, total: number) => {
  if (total <= 1) {
    return { x: 0, y: 0 };
  }

  if (total === 2) {
    return index === 0 ? { x: -14, y: 0 } : { x: 14, y: 0 };
  }

  const radius = total <= 4 ? 18 : 24;
  const angle = ((Math.PI * 2) / total) * index - Math.PI / 2;

  return {
    x: Math.round(Math.cos(angle) * radius),
    y: Math.round(Math.sin(angle) * radius)
  };
};

/**
 * Returns the URL of the numbered pin SVG for the given class index.
 * We only have art for pins 1-9; anything beyond that falls back to "9".
 */
const getPinUrl = (pinNum: number) => {
  const displayPinNum = Math.min(pinNum + 1, 9);
  return `/app/resources/images/map-pin-red0${displayPinNum}.svg`;
};

// ---------------------------------------------------------------------------
// Room / facility / level matching helpers
//
// Indoor data sources can format the same room/level/facility several
// different ways (e.g. "L104" vs "L0104", "00123" vs "123"). These helpers
// generate token variants so we can do robust cross-source matching.
// ---------------------------------------------------------------------------

/** Strip whitespace + non-alphanumerics and uppercase. */
const normalizeRoomToken = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Returns equivalent forms of a room token, including zero-padded /
 * zero-stripped variations so "L0104" and "L104" can match each other.
 */
const getRoomTokenVariants = (value: string) => {
  const token = normalizeRoomToken(value);
  if (!token) {
    return [] as string[];
  }
  const variants = new Set([token]);
  // Handle zero-padded room variants like L0104 <-> L104.
  variants.add(token.replace(/^([A-Z]*?)0+([0-9].*)$/, '$1$2'));
  variants.add(token.replace(/^0+([0-9].*)$/, '$1'));
  return Array.from(variants).filter(Boolean);
};

/**
 * Returns the candidate string forms of a facility/building number. The
 * structures layer may provide a numeric value while the indoors service
 * stores it as a zero-padded string, so we generate all reasonable forms.
 */
const getFacilityCandidates = (facilityId: string) => {
  const value = facilityId.trim();
  if (!value) {
    return [] as string[];
  }
  const numeric = String(Number(value));
  return Array.from(new Set([
    value,
    Number.isNaN(Number(value)) ? '' : numeric,
    value.padStart(5, '0'),
  ].filter(Boolean)));
};

/** True if any candidate form of two facility ids matches. */
const hasFacilityMatch = (leftFacilityId: string, rightFacilityId: string) => {
  const leftCandidates = new Set(getFacilityCandidates(leftFacilityId));
  const rightCandidates = getFacilityCandidates(rightFacilityId);
  return rightCandidates.some((candidate) => leftCandidates.has(candidate));
};

/**
 * Coerces the variety of selected-facility shapes returned by the indoors
 * store into a single string id suitable for comparison via
 * {@link hasFacilityMatch}.
 */
const getSelectedFacilityId = (value: unknown) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  const selected = value as SelectedFacilityLike;
  return String(
    selected.facilityId
      ?? selected.id
      ?? selected.bldg_num
      ?? selected.building_number
      ?? selected.attributes?.FACILITY_ID
      ?? selected.attributes?.bldg_num
      ?? selected.attributes?.building_number
      ?? ''
  );
};

/**
 * Splits the class location string (e.g. "WEB 1230" or "LCB, L104") into the
 * candidates that might represent the room: the trailing token, everything
 * after the building code, and the whole string. Callers feed these through
 * {@link getRoomTokenVariants} for matching.
 */
const getRoomCandidates = (classInfo: any) => {
  const location = String(classInfo?.attributes?.UU_CRSE_LOC ?? '').trim();
  if (!location) {
    return [] as string[];
  }

  const pieces = location
    .split(/[\s,]+/)
    .map((piece) => piece.trim())
    .filter(Boolean);
  const lastPiece = pieces.length ? pieces[pieces.length - 1] : '';
  const withoutBuilding = pieces.length > 1 ? pieces.slice(1).join(' ') : '';

  return [lastPiece, withoutBuilding, location]
    .map((candidate) => candidate.trim())
    .filter(Boolean);
};

/**
 * Returns a representative point for any geometry: the point itself for
 * point geometries, otherwise the center of the geometry's extent.
 */
const getGeometryCenter = (geometry: any) => {
  if (!geometry) {
    return null;
  }
  if (geometry.type === 'point') {
    return geometry;
  }
  return geometry.extent?.center ?? null;
};

/** Pulls a level id from any of the property names different sources use. */
const getLevelId = (level: LevelLike) => level.levelId ?? level.id ?? level.attributes?.LEVEL_ID;

/** @see normalizeRoomToken — same logic, reused for level identifiers. */
const normalizeLevelToken = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

/** Equivalent forms of a level token, mirroring {@link getRoomTokenVariants}. */
const getLevelTokenVariants = (value: string) => {
  const token = normalizeLevelToken(value);
  if (!token) {
    return [] as string[];
  }
  const variants = new Set([token]);
  variants.add(token.replace(/^([A-Z]*?)0+([0-9].*)$/, '$1$2'));
  variants.add(token.replace(/^0+([0-9].*)$/, '$1'));
  return Array.from(variants).filter(Boolean);
};

/**
 * Collects all token variants representing a level from any of the fields
 * different services use (LEVEL_ID, level_short, BldgLevel, NAME, etc.).
 */
const getLevelCandidates = (value: any) => {
  const candidates = [
    value?.levelId,
    value?.id,
    value?.name,
    value?.shortName,
    value?.attributes?.LEVEL_ID,
    value?.attributes?.level_short,
    value?.attributes?.BldgLevel,
    value?.attributes?.NAME,
    value?.attributes?.NAME_SHORT,
    value?.LEVEL_ID,
    value?.level_short,
    value?.BldgLevel,
  ]
    .map((v) => String(v ?? '').trim())
    .filter(Boolean);

  const tokens = new Set<string>();
  candidates.forEach((candidate) => {
    getLevelTokenVariants(candidate).forEach((token) => tokens.add(token));
  });
  return Array.from(tokens);
};

/**
 * Builds a stable key from a point's coordinates + spatial reference.
 * Used to detect when multiple pins land on the exact same location so we
 * can fan them out via {@link getPinOffset}.
 */
const getPointOverlapKey = (point: any) => {
  if (!point) {
    return '';
  }
  const x = Number(point.x ?? 0).toFixed(3);
  const y = Number(point.y ?? 0).toFixed(3);
  const wkid = point.spatialReference?.wkid ?? point.spatialReference?.latestWkid ?? 'none';
  return `${x}:${y}:${wkid}`;
};

/**
 * Finds `target` in `availableLevels` by comparing token candidates.
 * Returns -1 when no match (or no comparable tokens) can be found.
 */
const resolveLevelIndex = (availableLevels: LevelLike[] = [], target: any) => {
  const targetTokens = getLevelCandidates(target);
  if (!targetTokens.length) {
    return -1;
  }
  return availableLevels.findIndex((level) => {
    const levelTokens = getLevelCandidates(level);
    return levelTokens.some((token) => targetTokens.includes(token));
  });
};

/**
 * Determines whether the user must travel up or down (or neither) to get
 * from `activeLevel` to `targetLevel`. Used to render the up/down arrow
 * indicator next to a pin when the class is on a different floor than the
 * one currently being viewed.
 */
const getLevelDirection = (targetLevel: any, activeLevel: any, availableLevels: LevelLike[] = []) => {
  if (!targetLevel || !activeLevel) {
    return null;
  }
  const targetIndex = resolveLevelIndex(availableLevels, targetLevel);
  const activeIndex = resolveLevelIndex(availableLevels, activeLevel);
  if (targetIndex < 0 || activeIndex < 0 || targetIndex === activeIndex) {
    return null;
  }
  return targetIndex > activeIndex ? 'up' : 'down';
};

/**
 * Returns a numeric ordering for a level (higher = physically higher in the
 * building). Prefers the explicit VERTICAL_ORDER, falling back to
 * LEVEL_NUMBER, returning undefined when neither is present.
 */
const getLevelOrder = (value: any) => {
  const verticalOrder = Number(value?.VERTICAL_ORDER ?? value?.attributes?.VERTICAL_ORDER);
  if (!Number.isNaN(verticalOrder)) {
    return verticalOrder;
  }
  const levelNumber = Number(value?.LEVEL_NUMBER ?? value?.attributes?.LEVEL_NUMBER);
  if (!Number.isNaN(levelNumber)) {
    return levelNumber;
  }
  return undefined;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Component that displays numbered pins on the map for each class building.
 * Queries the ArcGIS structures layer to get building geometries and places
 * numbered pins (1-9) at the centroid of each building.
 *
 * When the indoors plugin is active and a facility/level is selected we also
 * resolve the specific room (unit) for each class so the pin can sit on the
 * actual classroom and an up/down arrow can hint at the floor difference.
 *
 * Renders nothing in the React tree — all visuals are added directly to the
 * ArcGIS map view.
 */
const MyClassesPins = () => {
  // --- Store / context wiring -------------------------------------------------
  const { payload: myClasses, isAuthenticated } = useAuthStore();
  const { setSelectedClassIndex, setMyClassesDetailOpen, setMyClassesOpen } = useUIStore();
  const { mapView, restEndpoint } = useMapStore();

  // The indoors hooks/constants are optional — UI library may not export them
  // in older builds, so we access them dynamically and tolerate undefined.
  const indoorsState = ((UiReact as any).useIndoorsStore as undefined | (() => any))?.();
  const selectedFacilityIdFromHook = ((UiReact as any).useIndoorsStoreSelectedFacilityId as undefined | (() => string | undefined))?.();
  const indoorsServiceUrl = (UiReact as any).INDOORS_SERVICE_URL as string | undefined;
  const indoorsSubLayerIndex = (UiReact as any).IndoorsSubLayerIndex as { Units?: number; Levels?: number } | undefined;

  // Currently-displayed indoor context: list of levels for the selected
  // facility, the active level index, and the facility id itself.
  const availableLevels = (indoorsState?.availableLevels ?? []) as LevelLike[];
  const activeLevelIndex = indoorsState?.activeLevelIndex as number | undefined;
  const selectedFacilityId = getSelectedFacilityId(
    selectedFacilityIdFromHook
      ?? indoorsState?.selectedFacilityId
      ?? indoorsState?.selectedFacility
      ?? ''
  );
  const activeLevelId =
    activeLevelIndex !== undefined && activeLevelIndex >= 0
      ? getLevelId(availableLevels[activeLevelIndex] ?? {})
      : undefined;

  // Track whether the current zoom is below the multipin threshold so we can
  // collapse co-located pins into a single "+" pin when zoomed out.
  const [isBelowMultipinThreshold, setIsBelowMultipinThreshold] = useState(
    (mapView?.zoom ?? 0) < MULTIPIN_ZOOM_THRESHOLD
  );

  // --- Watch zoom level -------------------------------------------------------
  // Keep `isBelowMultipinThreshold` in sync with the live map zoom. Only
  // updates state when the boolean actually flips to avoid re-renders on
  // every zoom tick.
  useEffect(() => {
    if (!mapView) {
      return;
    }

    setIsBelowMultipinThreshold(mapView.zoom < MULTIPIN_ZOOM_THRESHOLD);

    const zoomHandle = mapView.watch('zoom', (nextZoom: number) => {
      const nextIsBelowThreshold = nextZoom < MULTIPIN_ZOOM_THRESHOLD;
      setIsBelowMultipinThreshold((prev) => (prev === nextIsBelowThreshold ? prev : nextIsBelowThreshold));
    });

    return () => {
      zoomHandle.remove();
    };
  }, [mapView]);

  // --- Expose debug helpers on window -----------------------------------------
  // Adds a `window.__myclassmapDebug` namespace with utilities for inspecting
  // and toggling indoors layers. Useful when troubleshooting why a pin isn't
  // landing on the right room/floor in production.
  useEffect(() => {
    if (!mapView || typeof window === 'undefined') {
      return;
    }
    const map = mapView.map;
    if (!map) {
      return;
    }

    const debugWindow = window as any;
    const listIndoorsLayers = () => INDOORS_LAYER_IDS.map((id) => {
      const layer = map.findLayerById(id);
      return {
        id,
        exists: Boolean(layer),
        visible: Boolean(layer?.visible),
        definitionExpression: layer && 'definitionExpression' in layer
          ? (layer as any).definitionExpression ?? null
          : null,
      };
    });

    const setIndoorsLayerVisibility = (id: string, visible: boolean) => {
      const layer = map.findLayerById(id);
      if (!layer) {
        return false;
      }
      layer.visible = visible;
      return true;
    };

    debugWindow.__myclassmapDebug = {
      ...(debugWindow.__myclassmapDebug ?? {}),
      getMapView: () => mapView,
      listIndoorsLayers,
      hideAllIndoorsLayers: () => {
        INDOORS_LAYER_IDS.forEach((id) => setIndoorsLayerVisibility(id, false));
        return listIndoorsLayers();
      },
      showOnlyIndoorsLayer: (id: string) => {
        INDOORS_LAYER_IDS.forEach((layerId) => setIndoorsLayerVisibility(layerId, layerId === id));
        return listIndoorsLayers();
      },
      setIndoorsLayerVisibility,
    };

    if (isPinsDebugEnabled()) {
      console.warn('[MyClassesPins DEBUG] Exposed window.__myclassmapDebug helpers');
    }
  }, [mapView]);

  // --- Main pin rendering effect ----------------------------------------------
  // Re-runs whenever the underlying data (classes, indoors selection, zoom
  // bucket, etc.) changes. Owns the lifecycle of a single GraphicsLayer that
  // contains all class pins.
  useEffect(() => {
    // Don't render pins if map isn't ready or user isn't authenticated
    if (!mapView || !mapView.map || !isAuthenticated || !myClasses || myClasses.length === 0 || !restEndpoint) {
      return;
    }

    // Group classes by building so each building gets one query and we can
    // detect when a single building has multiple classes (multipin behavior).
    // Each class gets a numbered pin (1-9) based on its position in the list.
    const groupedBuildings = new Map<string, BuildingPin[]>();

    myClasses.forEach((classInfo, index) => {
      // Normalize building code to match ArcGIS structures layer
      const rawBldgCd = classInfo.attributes.BLDG_CD.trim();
      const bldgCd = normalizeBuildingCode(rawBldgCd);

      // Skip codes we know don't have a physical mappable location.
      if (UNMAPPED_BUILDING_CODES.has(rawBldgCd) || UNMAPPED_BUILDING_CODES.has(bldgCd)) {
        return;
      }

      const buildingPins = groupedBuildings.get(bldgCd) ?? [];
      buildingPins.push({
        bldgCd,
        classIndex: index,
        classInfo,
      });
      groupedBuildings.set(bldgCd, buildingPins);
    });

    if (groupedBuildings.size === 0) return;

    // Build a single SQL `IN (...)` clause to fetch all needed building
    // geometries in one round trip. Single quotes inside codes are escaped.
    const buildingCodes = Array.from(groupedBuildings.keys()).map(bldgCd => `'${bldgCd.replace(/'/g, "''")}'`).join(', ');

    // Create feature layer pointing to the structures layer in ArcGIS
    const structureFL = new FeatureLayer({
      url: buildLayerUrl(restEndpoint, LayerPaths.structures)
    });

    // Configure query to get building geometries
    const query = structureFL.createQuery();
    query.where = `ABBREVIATION IN (${buildingCodes})`; // Match building codes
    query.outFields = ['ABBREVIATION', 'OBJECTID', 'building_number'];
    query.returnGeometry = true; // Need geometries to place pins

    // `cancelled` guards against the async query resolving after unmount /
    // dependency change — without it we could add graphics to a stale layer.
    let cancelled = false;

    // Remove any pin layer left over from a previous run before adding ours.
    const existingLayer = mapView.map.findLayerById(MY_CLASSES_PINS_LAYER_ID);
    if (existingLayer) {
      mapView.map.remove(existingLayer);
    }

    const graphicsLayer = new GraphicsLayer({
      id: MY_CLASSES_PINS_LAYER_ID
    });
    mapView.map.add(graphicsLayer);

    // Wire up click handling on the map view: when a user clicks one of our
    // pin graphics, open the My Classes panel and select that class.
    const clickHandle = mapView.on('click', (event: any) => {
      mapView.hitTest(event).then((hitResult: any) => {
        const hitGraphic = hitResult?.results?.find((result: any) => {
          const graphic = result?.graphic;
          return graphic?.layer?.id === MY_CLASSES_PINS_LAYER_ID && Number.isInteger(graphic?.attributes?.classIndex);
        })?.graphic;

        if (!hitGraphic) {
          return;
        }

        // We render our own My Classes detail panel for class pins, so
        // suppress the default Esri popup if anything else tried to open it.
        mapView.popup?.close();

        setSelectedClassIndex(hitGraphic.attributes.classIndex);
        setMyClassesOpen(true);
        setMyClassesDetailOpen(true);
      }).catch(() => undefined);
    });

    // Execute query and place pins
    structureFL.queryFeatures(query).then(async (results) => {
      if (cancelled) {
        return;
      }

      // Index returned building features by abbreviation for O(1) lookup.
      const buildingByCode = new Map<string, any>();
      results.features.forEach((feature: any) => {
        const abbreviation = feature?.attributes?.ABBREVIATION;
        if (abbreviation) {
          buildingByCode.set(abbreviation, feature);
        }
      });

      debugLog(
        'Pin render state:',
        `selectedFacilityId=${selectedFacilityId}`,
        `activeLevelId=${activeLevelId}`,
        `availableLevels=${availableLevels.map((l) => getLevelId(l)).join(',')}`,
      );

      // -----------------------------------------------------------------
      // Indoor unit / level resolution
      //
      // If the indoors service is available, fetch the level metadata and
      // unit (room) geometries for the buildings we care about. This lets
      // us anchor pins on the actual classroom and compute up/down arrows
      // when the user is viewing a different floor.
      // -----------------------------------------------------------------
      const unitByFacilityAndRoom = new Map<string, UnitMatch>();
      const levelOrderByFacilityAndToken = new Map<string, number>();
      const levelByFacilityAndId = new Map<string, any>();
      if (indoorsServiceUrl && indoorsSubLayerIndex?.Units !== undefined) {
        // Collect the facility ids we need indoor data for, exploding each
        // into its candidate forms for fuzzy matching downstream.
        const facilityIdsToQuery = new Set<string>();
        groupedBuildings.forEach((_, bldgCd) => {
          const buildingFeature = buildingByCode.get(bldgCd);
          const rawFacilityId = String(buildingFeature?.attributes?.building_number ?? '').trim();
          if (!rawFacilityId) {
            return;
          }

          getFacilityCandidates(rawFacilityId).forEach((facilityCandidate) => {
            facilityIdsToQuery.add(facilityCandidate);
          });
        });

        debugLog('Facility IDs to query for units:', Array.from(facilityIdsToQuery).join(', '));

        if (facilityIdsToQuery.size) {
          // 1) Fetch level metadata (LEVEL_ID, NAME, VERTICAL_ORDER, ...)
          //    so we can compare floors and decide arrow direction.
          if (indoorsSubLayerIndex?.Levels !== undefined) {
            const levelsLayer = new FeatureLayer({
              url: `${indoorsServiceUrl}/${indoorsSubLayerIndex.Levels}`,
            });
            const levelsQuery = levelsLayer.createQuery();
            const levelFacilityIds = Array.from(new Set(Array.from(facilityIdsToQuery)
              .map((id) => String(Number(id)))
              .filter(Boolean)));
            levelsQuery.where = levelFacilityIds.map((id) => `FACILITY_ID = '${id}'`).join(' OR ');
            levelsQuery.outFields = ['FACILITY_ID', 'LEVEL_ID', 'NAME', 'NAME_SHORT', 'LEVEL_NUMBER', 'VERTICAL_ORDER'];
            levelsQuery.returnGeometry = false;

            await levelsLayer.queryFeatures(levelsQuery).then((levelResults) => {
              levelResults.features.forEach((levelFeature: any) => {
                const attrs = levelFeature?.attributes ?? {};
                const facilityId = String(attrs.FACILITY_ID ?? '').trim();
                const levelId = String(attrs.LEVEL_ID ?? '').trim();
                if (!facilityId || !levelId) {
                  return;
                }
                const order = getLevelOrder(attrs);
                const levelTokens = getLevelCandidates(attrs);
                // Index by every facility candidate so lookups by either
                // padded or numeric facility id will succeed.
                getFacilityCandidates(facilityId).forEach((facilityCandidate) => {
                  levelByFacilityAndId.set(`${facilityCandidate}::${levelId}`, attrs);
                  if (order !== undefined) {
                    levelTokens.forEach((levelToken) => {
                      levelOrderByFacilityAndToken.set(`${facilityCandidate}::${levelToken}`, order);
                    });
                  }
                });
                debugLog(
                  `Loaded level metadata: facility=${facilityId} levelId=${levelId} levelTokens=${levelTokens.join(',')} order=${String(order)}`,
                );
              });
            }).catch((error) => {
              console.error('Error querying indoors levels for class pins:', error);
            });
          }

          // 2) Fetch the unit (room) features for those buildings so each
          //    class can be matched to its specific classroom polygon.
          const unitsLayer = new FeatureLayer({
            url: `${indoorsServiceUrl}/${indoorsSubLayerIndex.Units}`,
          });
          const unitsQuery = unitsLayer.createQuery();
          // Use bldg_num field (integer) instead of FACILITY_ID which doesn't exist
          // Query with numeric values to match the integer field type
          const bldgNums = Array.from(new Set(Array.from(facilityIdsToQuery).map((id) => Number(id))))
            .map(id => {
            const num = Number(id);
            return Number.isNaN(num) ? null : num;
          }).filter((n): n is number => n !== null);

          if (!bldgNums.length) {
            debugLog('No valid building numbers found for units query');
            return;
          }

          unitsQuery.where = bldgNums.map(bldgNum => `bldg_num = ${bldgNum}`).join(' OR ');
          unitsQuery.outFields = ['bldg_num', 'LEVEL_ID', 'NAME', 'NAME_LONG', 'UNIT_ID', 'sp_loc_code', 'gis_loc_code', 'level_short'];
          unitsQuery.returnGeometry = true;
          debugLog(`Units query WHERE clause: ${unitsQuery.where}`);

          await unitsLayer.queryFeatures(unitsQuery).then((unitResults) => {
            unitResults.features.forEach((unitFeature: any) => {
              const bldgNum = unitFeature?.attributes?.bldg_num;
              if (!bldgNum) {
                return;
              }
              const facilityId = String(bldgNum);
              // Use NAME and NAME_LONG fields (NAME_SHORT doesn't exist in this service)
              const unitNames = [
                unitFeature?.attributes?.NAME,
                unitFeature?.attributes?.NAME_LONG,
                unitFeature?.attributes?.UNIT_ID,
                unitFeature?.attributes?.sp_loc_code,
                unitFeature?.attributes?.gis_loc_code,
              ]
                .map((value: unknown) => String(value ?? '').trim())
                .filter(Boolean);
              // Index this unit under every (facility candidate, room token)
              // combination so room-name lookups succeed regardless of which
              // form the class record uses.
              unitNames.forEach((roomName) => {
                const roomTokens = getRoomTokenVariants(roomName);
                getFacilityCandidates(facilityId).forEach((facilityCandidate) => {
                  roomTokens.forEach((roomToken) => {
                    const unitLevelId = String(unitFeature?.attributes?.LEVEL_ID ?? '').trim();
                    const levelAttrs = levelByFacilityAndId.get(`${facilityCandidate}::${unitLevelId}`);
                    unitByFacilityAndRoom.set(`${facilityCandidate}::${roomToken}`, {
                      feature: unitFeature,
                      level: levelAttrs ?? unitFeature?.attributes ?? {},
                    });
                    debugLog(
                      `Stored unit: facility=${facilityCandidate} room=${roomName} (token=${roomToken}) levelId=${unitLevelId}`,
                    );
                  });
                });
              });
            });
            debugLog(`Loaded ${unitResults.features.length} units.`);
          }).catch((error) => {
            console.error('Error querying indoors units for class pins:', error);
          });
        }
      }

      // -----------------------------------------------------------------
      // Render pins, one building at a time
      // -----------------------------------------------------------------
      groupedBuildings.forEach((buildingPins, bldgCd) => {
        // Find the building feature that matches this class
        const feature = buildingByCode.get(bldgCd);

        if (feature && feature.geometry) {
          // Calculate center point of building polygon
          const centroid = (feature.geometry as any).extent?.center;

          if (centroid) {
            // Create point geometry at building centroid (used as the
            // fallback anchor when no specific room can be resolved).
            const point = new Point({
              x: centroid.x,
              y: centroid.y,
              spatialReference: feature.geometry.spatialReference
            });

            const facilityId = String(feature?.attributes?.building_number ?? '');
            // True when the user has drilled into this building's interior
            // and selected a level — that combination tells us we should
            // render every class individually rather than collapsing them.
            const isFloorFilteredForBuilding = Boolean(
              selectedFacilityId
              && hasFacilityMatch(selectedFacilityId, facilityId)
              && activeLevelId
            );
            // Collapse to a single "+" pin only when zoomed out, multiple
            // classes share this building, and we're not actively viewing
            // its interior.
            const showMultipin = buildingPins.length > 1 && isBelowMultipinThreshold && !isFloorFilteredForBuilding;
            const pinsToRender = showMultipin ? [buildingPins[0]] : buildingPins;

            // For each pin we resolve: which point to anchor at (room or
            // building center), and which up/down arrow indicator to show.
            const plannedPins = pinsToRender.map((buildingPin) => {
              // Try every room candidate × token variant against the unit
              // index until something hits, scoped to this building.
              const roomCandidates = getRoomCandidates(buildingPin.classInfo);
              const roomTokens = roomCandidates.flatMap((roomCandidate) => getRoomTokenVariants(roomCandidate));
              const facilityCandidates = getFacilityCandidates(facilityId);
              let unitMatch: UnitMatch | undefined;
              for (const facilityCandidate of facilityCandidates) {
                const matchingRoomToken = roomTokens.find((token) => unitByFacilityAndRoom.has(`${facilityCandidate}::${token}`));
                if (matchingRoomToken) {
                  unitMatch = unitByFacilityAndRoom.get(`${facilityCandidate}::${matchingRoomToken}`);
                  debugLog(
                    `Class ${buildingPin.classIndex}: matched room token=${matchingRoomToken} facility=${facilityCandidate} unitLevelId=${unitMatch?.level?.LEVEL_ID ?? unitMatch?.level?.level_short ?? unitMatch?.level?.BldgLevel ?? ''}`,
                  );
                  break;
                }
              }
              const unitCenter = getGeometryCenter(unitMatch?.feature?.geometry);
              // Only use room-precise placement when this building is the
              // currently-selected facility — otherwise the user is looking
              // at exterior data and a building centroid is more useful.
              const appliesCurrentFacilityLevel = Boolean(
                selectedFacilityId
                && hasFacilityMatch(selectedFacilityId, facilityId)
              );
              const useRoomPlacement = Boolean(appliesCurrentFacilityLevel && activeLevelId);

              // Compute the up/down indicator. We prefer comparing
              // VERTICAL_ORDER values (most reliable across naming
              // conventions); fall back to index-in-availableLevels if we
              // don't have orders for one of the floors.
              let levelDirection = null as 'up' | 'down' | null;
              if (appliesCurrentFacilityLevel) {
                const activeTokens = getLevelCandidates(availableLevels[activeLevelIndex ?? -1] ?? {});
                const targetTokens = getLevelCandidates(unitMatch?.level ?? {});
                const activeOrder = facilityCandidates
                  .flatMap((facilityCandidate) => activeTokens.map((token) => levelOrderByFacilityAndToken.get(`${facilityCandidate}::${token}`)))
                  .find((value) => value !== undefined);
                const targetOrder = facilityCandidates
                  .flatMap((facilityCandidate) => targetTokens.map((token) => levelOrderByFacilityAndToken.get(`${facilityCandidate}::${token}`)))
                  .find((value) => value !== undefined);
                debugLog(
                  `Class ${buildingPin.classIndex}: activeLevelTokens=${activeTokens.join(',')} targetLevelTokens=${targetTokens.join(',')} activeOrder=${String(activeOrder)} targetOrder=${String(targetOrder)}`,
                );
                if (activeOrder !== undefined && targetOrder !== undefined && activeOrder !== targetOrder) {
                  levelDirection = targetOrder > activeOrder ? 'up' : 'down';
                } else {
                  levelDirection = getLevelDirection(unitMatch?.level, availableLevels[activeLevelIndex ?? -1], availableLevels);
                }
              }
              const anchorPoint = useRoomPlacement ? (unitCenter ?? point) : point;

              debugLog(
                `Class ${buildingPin.classIndex}: appliesCurrentFacilityLevel=${appliesCurrentFacilityLevel} useRoomPlacement=${useRoomPlacement} levelDirection=${levelDirection}`,
              );

              return {
                anchorPoint,
                buildingPin,
                levelDirection,
              };
            });

            // Detect pins that share an exact anchor so we can fan them out.
            // This typically happens when room placement falls back to the
            // building centroid for several classes in the same building.
            const overlapCountByPoint = new Map<string, number>();
            plannedPins.forEach(({ anchorPoint }) => {
              const key = getPointOverlapKey(anchorPoint);
              if (!key) {
                return;
              }
              overlapCountByPoint.set(key, (overlapCountByPoint.get(key) ?? 0) + 1);
            });
            const overlapIndexByPoint = new Map<string, number>();

            // Finally, materialize each planned pin into ArcGIS graphics.
            plannedPins.forEach(({ anchorPoint, buildingPin, levelDirection }) => {
              const overlapKey = getPointOverlapKey(anchorPoint);
              const overlapTotal = overlapKey ? (overlapCountByPoint.get(overlapKey) ?? 1) : 1;
              const overlapIndex = overlapKey ? (overlapIndexByPoint.get(overlapKey) ?? 0) : 0;
              if (overlapKey) {
                overlapIndexByPoint.set(overlapKey, overlapIndex + 1);
              }

              // Multipin already shows a single combined marker, so no fan-out.
              const offset = showMultipin || overlapTotal <= 1
                ? { x: 0, y: 0 }
                : getPinOffset(overlapIndex, overlapTotal);

              // Create numbered pin symbol (1-9) — or the multipin "+" art
              // when collapsing several classes into one marker.
              const pinSymbol = new PictureMarkerSymbol({
                angle: 0,
                xoffset: offset.x,
                yoffset: 15 + offset.y, // Offset to position pin point at building
                url: showMultipin ? '/app/resources/images/map-pin-red-plus.svg' : getPinUrl(buildingPin.classIndex),
                width: showMultipin ? 34 : 30,
                height: showMultipin ? 34 : 30
              });

              // Create graphic with pin symbol. No popupTemplate is set —
              // clicking a pin opens the in-app My Classes detail panel via
              // the map click handler above instead of the default Esri
              // popup.
              const graphic = new Graphic({
                geometry: anchorPoint,
                symbol: pinSymbol,
                attributes: {
                  bldgCd,
                  classCount: buildingPins.length,
                  classIndex: buildingPin.classIndex,
                  pinNum: buildingPin.classIndex + 1,
                  levelDirection,
                }
              });

              // Add graphic to layer and layer to map
              graphicsLayer.add(graphic);

              // When the class is on a different floor than the one being
              // viewed, drop a small ↑/↓ glyph above the pin so the user
              // knows they need to change levels to reach it.
              if (levelDirection && !showMultipin) {
                const indicatorGraphic = new Graphic({
                  geometry: anchorPoint,
                  symbol: new TextSymbol({
                    text: levelDirection === 'up' ? '↑' : '↓',
                    // Match the red of the class pin (#cc0000) for a visually
                    // cohesive indicator. White halo gives it strong contrast
                    // against any basemap.
                    color: '#cc0000',
                    haloColor: '#ffffff',
                    haloSize: 3,
                    font: {
                      size: 18,
                      family: 'Arial',
                      weight: 'bold',
                    },
                    yoffset: 34,
                  }),
                  attributes: {
                    classIndex: buildingPin.classIndex,
                    isLevelIndicator: true,
                  },
                });
                graphicsLayer.add(indicatorGraphic);
              }
            });
          }
        }
      });
    }).catch((error) => {
      console.error('Error querying buildings for class pins:', error);
    });

    // Cleanup: cancel any in-flight async work, detach the click listener,
    // and remove our graphics layer so we don't leak across re-renders.
    return () => {
      cancelled = true;
      clickHandle.remove();
      if (mapView?.map) {
        mapView.map.remove(graphicsLayer);
      }
    };
  }, [
    mapView,
    myClasses,
    isAuthenticated,
    restEndpoint,
    isBelowMultipinThreshold,
    setMyClassesDetailOpen,
    setMyClassesOpen,
    setSelectedClassIndex,
    indoorsServiceUrl,
    indoorsSubLayerIndex?.Units,
    indoorsSubLayerIndex?.Levels,
    availableLevels,
    activeLevelId,
    selectedFacilityId,
  ]);

  // This component is a side-effect-only renderer; all output goes to the
  // ArcGIS map view via the GraphicsLayer created above.
  return null;
};

export default MyClassesPins;
